import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { OFFERINGS, CASE_STUDIES, LAYERS } from '../mock';
import NeuralWorksBusinessViz from '../components/NeuralWorksBusinessViz';
import ClientShowreel from '../components/ClientShowreel';
import ClosingCTA from '../components/ClosingCTA';
import PulseCTA from '../components/PulseCTA';
import SEO from '../components/SEO';

export default function Home() {
  const handleScrollToProof = () => {
    const el = document.getElementById('proof-bar');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="bg-[#FBFAF6]">
      <SEO
        title={null}
        description="DigitalWorks is an AI-native transformation and engineering consulting partner. We advise, build and operate AI-native products for global retail, luxury and consumer groups — under NeuralWorks, our in-house delivery framework."
        canonical="https://digitalworks.co/"
      />
      {/* HERO */}
      <section className="relative pt-6 md:pt-10 pb-8">
        <div className="dwx">
          {/* Small marker line, echoing Vailu */}
          <div className="relative h-[2px] w-[220px] bg-[#22A94F] mb-8 md:mb-10" />

          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12">
              <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-6 rise">
                DigitalWorks · Consulting Group
              </div>
              {/* H1 — the positioning statement */}
              <h1 className="serif-tight text-[54px] md:text-[88px] xl:text-[108px] 2xl:text-[124px] leading-[0.98] text-[#1A1A1A] rise" style={{ animationDelay: '60ms' }}>
                DigitalWorks is an <span className="text-[#22A94F]">AI-native transformation</span> &amp; engineering consulting partner.
              </h1>
            </div>

            {/* H2 + right-side info sit under the H1, clearly subordinated */}
            <div className="col-span-12 lg:col-span-8 rise" style={{ animationDelay: '140ms' }}>
              <p className="text-[18px] md:text-[22px] leading-[1.5] text-[#1A1A1A]/72 mt-6 max-w-3xl">
                We advise, build and operate the products you actually put in front of customers &mdash; backed by our years running programmes for global brands, and by our in-house engineering framework, <span className="text-[#1A1A1A] font-medium">NeuralWorks</span>.
              </p>
              {/* Inline "talk to us" CTA — closes the gap between hero and the main "Book a working session" CTA further down the page. */}
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px] text-[#1A1A1A]/70">
                <span className="text-[#1A1A1A]/55">Curious how this would land on your estate?</span>
                <PulseCTA to="/contact" size="sm" variant="green" testId="hero-inline-book-session">
                  Talk to us — book a working session
                </PulseCTA>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-4 rise" style={{ animationDelay: '180ms' }}>
              <div className="mt-6 md:mt-8 rounded-[18px] bg-[#F4F2EC] border border-[#E8E4D9] p-5">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50 mb-2">
                  Global delivery
                </div>
                <div className="text-[13px] leading-relaxed text-[#1A1A1A]/75">
                  We have run digital commerce and transformation programmes for clients across MEA, Europe and Asia &mdash; global beauty leaders, luxury groups, diversified conglomerates and specialty retailers. The habits we built there now travel into every new engagement.
                </div>
              </div>
            </div>
          </div>

          {/* Hero card row — vailu inspired, BCG-clarity revisions */}
          <div className="grid grid-cols-12 gap-4 md:gap-6 mt-16 md:mt-20">
            {/* Deep-green Impact card — high contrast, BCG-style */}
            <div className="col-span-12 md:col-span-5 rounded-[28px] bg-[#0F3D22] p-10 min-h-[280px] flex flex-col justify-between lift">
              <div>
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#7ED4A0] mb-6">Client outcomes</div>
                <h3 className="serif text-[30px] md:text-[34px] leading-[1.02] text-[#FBFAF6]">Enterprise delivery,<br />shipped in production.</h3>
              </div>
              <p className="text-[14px] leading-relaxed text-[#FBFAF6]/85">
                We have run digital commerce and transformation programmes for clients across MEA, Europe and Asia &mdash; global beauty leaders, luxury groups, diversified conglomerates and specialty retailers.
              </p>
              <Link to="/work" className="inline-flex items-center gap-2 mt-6 text-[14px] font-semibold text-[#22A94F] hover:text-[#7ED4A0] transition-colors">
                Explore case studies <ArrowRight size={16} />
              </Link>
            </div>

            {/* Middle spacer card with animated scroll button */}
            <button
              type="button"
              onClick={handleScrollToProof}
              aria-label="See the outcomes"
              className="col-span-12 md:col-span-3 rounded-[28px] bg-[#F4F2EC] hover:bg-[#E8E4D9] p-10 min-h-[280px] flex items-center justify-center lift transition-colors group cursor-pointer text-left"
            >
              <div className="flex flex-col items-center gap-4 text-[#1A1A1A]/70 group-hover:text-[#22A94F] transition-colors">
                <motion.svg
                  width="40" height="70" viewBox="0 0 40 70" fill="none"
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <motion.path
                    d="M20 0 V60"
                    stroke="currentColor" strokeWidth="1.6"
                    initial={{ pathLength: 0.2 }}
                    animate={{ pathLength: [0.2, 1, 0.2] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <motion.path
                    d="M8 48 L20 60 L32 48"
                    stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </motion.svg>
                <span className="mono text-[10px] tracking-[0.22em] uppercase">See the outcomes</span>
              </div>
            </button>

            {/* Dark card */}
            <div className="col-span-12 md:col-span-4 rounded-[28px] bg-[#1A1A1A] p-10 min-h-[280px] flex flex-col justify-between lift">
              <div>
                <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-6">The framework</div>
                <h3 className="serif text-[30px] md:text-[34px] leading-[1.02] text-[#FBFAF6]">Delivery — just like a flight plan.</h3>
              </div>
              <p className="text-[14px] leading-relaxed text-[#FBFAF6]/70">
                Four layers. Two review points. One rule: our engineers draft and refine the work, and two people review it together before anything reaches your customers.
              </p>
              <Link to="/neuralworks" className="inline-flex items-center gap-2 mt-6 text-[14px] font-medium text-[#22A94F]">
                How it works <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT SHOWREEL — BCG-style impact band + centered marquee */}
      <ClientShowreel />

      {/* IMPACT METRICS — qualified numbers: every stat says what it delivered and how */}
      <section id="proof-bar" className="py-20 scroll-mt-24">
        <div className="dwx">
          <div className="rounded-[28px] bg-[#0F3D22] p-8 md:p-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#7ED4A0] mb-3">Impact metrics — what we deliver, and how</div>
                <h3 className="serif-tight text-[32px] md:text-[44px] leading-[1.02] text-[#FBFAF6] max-w-xl">
                  Numbers that name the thing they moved.
                </h3>
              </div>
              <p className="text-[13px] leading-relaxed text-[#FBFAF6]/65 max-w-sm">
                Every figure below is qualified — what changed, on whose estate, and under what discipline. No headline percentage without the sentence that earns it.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
              <div className="border-l-2 border-[#22A94F] pl-5">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0] mb-3">Engagement cycle time</div>
                <div className="serif-tight text-[64px] leading-none text-[#FBFAF6]">4&ndash;6 weeks</div>
                <div className="mt-4 text-[15px] leading-relaxed text-[#FBFAF6]/80">From a first working session to a costed first increment ready to commit to. The same shape, repeatedly, whichever client we are working with.</div>
                <div className="mt-3 mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]/80">Our engagement model</div>
              </div>
              <div className="border-l-2 border-[#22A94F] pl-5">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0] mb-3">Support resolution time</div>
                <div className="serif-tight text-[64px] leading-none text-[#FBFAF6]">38% <span className="text-[36px] text-[#FBFAF6]/60">faster</span></div>
                <div className="mt-4 text-[15px] leading-relaxed text-[#FBFAF6]/80">Reduction in tier-1 ticket resolution time via agent-assisted triage running on shadow data, with a human approving every customer-facing action.</div>
                <div className="mt-3 mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]/80">Run &amp; Optimise engagements</div>
              </div>
              <div className="border-l-2 border-[#22A94F] pl-5">
                <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0] mb-3">Delivery leverage</div>
                <div className="serif-tight text-[64px] leading-none text-[#FBFAF6] whitespace-nowrap">6 <span className="text-[36px] text-[#FBFAF6]/60">workstreams</span></div>
                <div className="mt-4 text-[15px] leading-relaxed text-[#FBFAF6]/80">Run in parallel by a single engineer through NeuralWorks &mdash; the way we run agent-assisted delivery, proven inside our own team before we took it to any client.</div>
                <div className="mt-3 mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]/80">DigitalWorks Internal Programme</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 bg-[#F4F2EC]">
        <div className="dwx grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">The problem</div>
            <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.98] mt-4 text-[#1A1A1A]">
              Everyone has an AI strategy.
              <span className="block text-[#1A1A1A]/45 select-none">Almost nobody has AI in production.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 space-y-6 text-[16px] leading-relaxed text-[#1A1A1A]/80">
            <p>The evidence points both ways at once. Inside one leading payments platform, autonomous agents already merge more than a thousand pull requests a week. Inside a well-known developer tools company, an agent deleted a live production database and fabricated thousands of records to cover the trail. A large consumer fintech replaced hundreds of support agents with AI, claimed a headline profit number, watched customer satisfaction collapse and quietly started rehiring.</p>
            <p>The difference is not the model. It is what surrounds it.</p>
            <p><span className="text-[#1A1A1A] font-medium">AI in 2026 does not make technical mistakes. It makes requirement mistakes.</span> When a requirement is unclear, an agent invents an assumption — an ordinary human failure, running at machine speed.</p>
            <div className="pt-4 flex flex-wrap gap-3">
              {['Drift', 'Unstated Requirements', 'Regressions'].map((t) => (
                <span key={t} className="px-4 py-2 rounded-full bg-[#FBFAF6] border border-[#1A1A1A]/15 text-[13px] text-[#1A1A1A]">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-24">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">What we do</div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.98] mt-4">One team from strategy to production.</h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-[15px] leading-relaxed text-[#1A1A1A]/70">Most engagements fail at the handoffs. We do not hand off. The people who write the assessment are accountable for what reaches production.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {OFFERINGS.map((o, i) => {
              // Alternating cream/coloured checkerboard: green · cream · cream · ink
              const variants = [
                { bg: '#0F3D22', text: '#FBFAF6', sub: 'text-[#FBFAF6]/80', eyebrow: 'text-[#7ED4A0]', arrow: 'text-[#FBFAF6]/60 group-hover:text-[#7ED4A0]' },
                { bg: '#F4F2EC', text: '#1A1A1A', sub: 'text-[#1A1A1A]/72', eyebrow: 'text-[#178838]', arrow: 'text-[#1A1A1A]/40 group-hover:text-[#22A94F]' },
                { bg: '#F4F2EC', text: '#1A1A1A', sub: 'text-[#1A1A1A]/72', eyebrow: 'text-[#178838]', arrow: 'text-[#1A1A1A]/40 group-hover:text-[#22A94F]' },
                { bg: '#1A1A1A', text: '#FBFAF6', sub: 'text-[#FBFAF6]/72', eyebrow: 'text-[#22A94F]', arrow: 'text-[#FBFAF6]/55 group-hover:text-[#22A94F]' },
              ];
              const v = variants[i % 4];
              return (
                <Link
                  key={o.slug}
                  to={`/what-we-do/${o.slug}`}
                  className="group rounded-[24px] p-10 transition-colors lift min-h-[240px] flex flex-col"
                  style={{ backgroundColor: v.bg, color: v.text }}
                >
                  <div className="flex items-start justify-between">
                    <div className={`mono text-[11px] tracking-[0.22em] uppercase ${v.eyebrow}`}>0{i + 1} · Capability</div>
                    <ArrowUpRight size={22} className={`transition-all group-hover:-translate-y-0.5 ${v.arrow}`} />
                  </div>
                  <h3 className="serif text-[30px] leading-tight mt-10">{o.title}</h3>
                  <p className={`mt-4 text-[14px] leading-relaxed ${v.sub}`}>{o.blurb}</p>
                  <div className={`mt-auto pt-8 text-[13px] font-medium inline-flex items-center gap-1.5 ${v.eyebrow}`}>
                    Explore <ArrowUpRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEURALWORKS TEASER */}
      <section className="py-24 bg-[#1A1A1A] text-[#FBFAF6]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F] mb-6">The framework</div>
              <h2 className="serif-tight text-[54px] md:text-[80px] leading-[0.95]">
                NeuralWorks.
                <br />
                <span className="text-[#FBFAF6]/55">AI delivery — just like a flight plan.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/75">
                One system. Four layers, always present. A single continuous flight from proposal to production, with every action recorded, classified and reviewed.
              </p>
            </div>
          </div>

          <NeuralWorksBusinessViz />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-10">
            {LAYERS.map((l) => (
              <div key={l.id} className="rounded-[18px] bg-[#2B2B2B] p-6 border border-[#FBFAF6]/8">
                <div className="flex items-center gap-3">
                  <span className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">{l.id}</span>
                </div>
                <div className="serif text-[22px] mt-2 leading-tight">{l.functionalName}</div>
                <div className="mono text-[11px] text-[#FBFAF6]/50 mt-1">— {l.metaphor}</div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/neuralworks" className="inline-flex items-center gap-2 text-[#22A94F] text-[15px] font-medium hover:text-[#7ED4A0] transition-colors">
              See NeuralWorks in full <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="py-24 bg-[#F4F2EC] border-y border-[#E8E4D9]">
        <div className="dwx">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#22A94F]">The work</div>
              <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.98] mt-4">What we have actually shipped.</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {CASE_STUDIES.slice(0, 3).map((c, i) => {
              const cardStyles = [
                { bg: '#0F3D22', ink: '#FBFAF6', sub: 'text-[#FBFAF6]/75', eyebrow: 'text-[#7ED4A0]', accent: '#22A94F', border: 'border-[#7ED4A0]/25' },
                { bg: '#FBFAF6', ink: '#1A1A1A', sub: 'text-[#1A1A1A]/75', eyebrow: 'text-[#178838]', accent: '#178838', border: 'border-[#E8E4D9]' },
                { bg: '#1A1A1A', ink: '#FBFAF6', sub: 'text-[#FBFAF6]/72', eyebrow: 'text-[#22A94F]', accent: '#22A94F', border: 'border-[#FBFAF6]/12' },
              ];
              const s = cardStyles[i];
              return (
                <Link key={c.slug} to={`/work/${c.slug}`} className="group rounded-[24px] p-8 lift" style={{ backgroundColor: s.bg, color: s.ink }}>
                  <div className="flex justify-between items-center">
                    <span className={`mono text-[11px] tracking-[0.22em] uppercase ${s.eyebrow}`}>{c.tag}</span>
                    <ArrowUpRight size={18} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {/* Client name rendered in editorial serif — reads reliably on any card background and matches the site's typographic system */}
                  <div className="mt-6 h-14 flex items-end">
                    <div
                      className="serif-tight leading-none text-[28px] md:text-[32px]"
                      style={{ color: s.ink }}
                      data-testid={`case-card-client-${c.slug}`}
                    >
                      {c.client.split(' · ')[0]}
                    </div>
                  </div>
                  <div className={`text-[12px] mono uppercase tracking-wider mt-3 ${s.sub}`}>{c.sector}</div>
                  <p className={`text-[14px] leading-relaxed mt-6 ${s.sub}`}>{c.problem}</p>
                  <div className={`mt-8 pt-6 border-t ${s.border}`}>
                    <div className="serif-tight text-[22px]" style={{ color: s.accent }}>{c.outcome}</div>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/work" className="inline-flex items-center gap-2 text-[15px] font-medium text-[#1A1A1A]">All case studies <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
      {/* UTILITIES — customer-available tools */}
      <section className="py-24">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-4">Available to you</div>
          <h2 className="serif-tight text-[42px] md:text-[64px] leading-[0.98] mb-14">Two ways to start with us today.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Utility 1 — Diagnostic tool */}
            <Link to="/tools/ai-readiness-diagnostic" className="group rounded-[24px] bg-[#F4F2EC] border border-[#1A1A1A]/12 p-10 lift flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F]">Free · 10 min</div>
                <div className="w-11 h-11 rounded-full bg-[#FBFAF6] flex items-center justify-center text-[#22A94F] group-hover:bg-[#22A94F] group-hover:text-[#FBFAF6] transition-colors">
                  <ArrowUpRight size={20} />
                </div>
              </div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50">Tool 01</div>
              <h3 className="serif text-[32px] leading-tight mt-3">AI Readiness Diagnostic</h3>
              <p className="text-[15px] leading-relaxed text-[#1A1A1A]/75 mt-5 flex-1">A 12-question assessment across specification, execution, governance and evidence. You receive a scored profile, your stage on the adoption path, and the three blockers to your next stage. No call required.</p>
              <div className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold text-[#178838] group-hover:text-[#22A94F]">
                Take the diagnostic <ArrowRight size={16} />
              </div>
            </Link>

            {/* Utility 2 — Working session */}
            <Link to="/contact" className="group rounded-[24px] bg-[#0F3D22] p-10 lift flex flex-col text-[#FBFAF6]">
              <div className="flex items-center justify-between mb-8">
                <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#7ED4A0]">Working session · 60 min</div>
                <div className="w-11 h-11 rounded-full bg-[#FBFAF6]/10 flex items-center justify-center text-[#7ED4A0] group-hover:bg-[#22A94F] group-hover:text-[#0F3D22] transition-colors">
                  <ArrowUpRight size={20} />
                </div>
              </div>
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]/70">Tool 02</div>
              <h3 className="serif text-[32px] leading-tight mt-3 text-[#FBFAF6]">Book a working session</h3>
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/80 mt-5 flex-1">Sixty minutes with two of our engineers. Bring a delivery problem. Leave with a written assessment of where AI would help, where it would not, and what finding out would involve. No pitch deck. No obligation.</p>
              <div className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold text-[#22A94F] group-hover:text-[#7ED4A0]">
                Book a session <ArrowRight size={16} />
              </div>
            </Link>
          </div>
        </div>
      </section>
<ClosingCTA />
    </div>
  );
}
